import React from 'react';
import type { MenuProps } from 'antd';
import { Layout, Menu, Avatar, Row, Divider, Button, Typography, Col, Dropdown } from 'antd';

import { ChartPie, Calendar, LogOut, Eye, Settings } from 'lucide-react';

import { Logo } from './logo';
import { useAuthContext } from '../context/auth';
import { DashboardProvider } from '../context/dashboard';
import { CardProvider } from '../context/card';
import { useWidgetContext } from '../context/widget';

import { DashboardPage } from '../pages/dashboard';
import { ContentPage } from '../pages/content';
import { WidgetPreviewPage } from '../pages/preview';
import { SettingsPage } from '../pages/settings';

import { WidgetSelector } from './widgets/widget-selector';
import { PersonalSettingsPage } from '../pages/personal-settings';

const { Content, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

interface MenuConfig {
  items: MenuItem[];
  components: Record<string, React.ReactNode>;
}

interface UserProfile {
  'custom:clientId'?: string;
  'custom:defaultWidgetId'?: string;
  email?: string;
}

interface UserProfileSectionProps {
  userInfo?: UserProfile;
  onSignOut: () => void;
}

const menuItemStyle = `
.ant-menu-item.ant-menu-item-selected {
  font-weight: bold;
}
`;

// Componente que muestra la información del perfil del usuario.
const UserProfileSection: React.FC<UserProfileSectionProps & { onKeySelect: (key: MenuSections) => void }> = ({
  userInfo,
  onSignOut,
  onKeySelect
}) => {
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'personal-settings',
      label: 'Configuración Personal'
    }
  ];

  return (
    <Row
      style={{
        bottom: '30px',
        position: 'absolute',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Dropdown
        menu={{
          items: dropdownItems,
          onClick: ({ key }) => onKeySelect(key as MenuSections)
        }}
        placement='topLeft'
        trigger={['click']}
      >
        <Row
          style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '10px',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxHeight: '50px',
            cursor: 'pointer'
          }}
        >
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar size={30}>
              {userInfo?.['custom:clientId']
                ?.split(' ')
                .map(word => word[0]?.toUpperCase())
                .join('') || '?'}
            </Avatar>
          </Col>
          <Col style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Typography.Text style={{ fontWeight: 'bold', color: '#595959', fontSize: 12 }}>
              {userInfo?.['custom:clientId']}
            </Typography.Text>
            <Typography.Text style={{ color: '#8c8c8c', fontSize: 12 }}>{userInfo?.email}</Typography.Text>
          </Col>
        </Row>
      </Dropdown>

      <Divider style={{ width: '100%', margin: '0 0 16px 0' }} />
      <Button type='text' icon={<LogOut width={14} />} onClick={onSignOut} style={{ color: 'rgba(109, 109, 109, 1)' }}>
        Cerrar sesión
      </Button>
    </Row>
  );
};

type MenuSections = 'dashboard' | 'content-editor' | 'preview' | 'settings' | 'personal-settings';

const AppSider: React.FC<{
  activeKey: MenuSections;
  items: MenuItem[];
  userInfo?: UserProfile;
  onKeySelect: (key: MenuSections) => void;
  onSignOut: () => void;
}> = ({ activeKey, items, userInfo, onKeySelect, onSignOut }) => {
  return (
    <Sider
      width={200}
      style={{
        background: '#FFFFFF',
        borderRight: '1px solid #f0f0f0'
      }}
    >
      <Row>
        <div style={{ padding: '20px' }}>
          <Logo />
        </div>

        <WidgetSelector />

        <style>{menuItemStyle}</style>
        <Menu
          mode='inline'
          selectedKeys={[activeKey]}
          onClick={({ key }) => onKeySelect(key as MenuSections)}
          items={items}
        />
      </Row>
      <UserProfileSection userInfo={userInfo} onSignOut={onSignOut} onKeySelect={onKeySelect} />
    </Sider>
  );
};

const generateMenuItems = (setActiveKey: (key: MenuSections) => void, widgetId: string): MenuConfig => {
  const components: Record<MenuSections, React.ReactNode> = {
    dashboard: (
      <DashboardProvider widgetId={widgetId}>
        <DashboardPage onNavigateToContent={() => setActiveKey('content-editor')} />
      </DashboardProvider>
    ),
    'content-editor': (
      <CardProvider>
        <ContentPage widgetId={widgetId} />
      </CardProvider>
    ),
    preview: <WidgetPreviewPage widgetId={widgetId} />,
    settings: <SettingsPage widgetId={widgetId} />,
    'personal-settings': <PersonalSettingsPage />
  };

  const items: MenuItem[] = [
    {
      key: 'dashboard',
      icon: <ChartPie width={14} />,
      label: 'Dashboard'
    },
    {
      key: 'content-editor',
      icon: <Calendar width={14} />,
      label: 'Contenido'
    },
    {
      key: 'preview',
      icon: <Eye width={14} />,
      label: 'Vista previa'
    },
    {
      key: 'settings',
      icon: <Settings width={14} />,
      label: 'Configuración'
    }
  ];

  return { items, components };
};

export const AppLayout: React.FC = () => {
  const { isLoggedIn, loginUrl, userInfo, signOut } = useAuthContext();
  const { selectedWidgetId } = useWidgetContext();
  const [activeKey, setActiveKey] = React.useState<MenuSections>('content-editor');

  const { items, components } = React.useMemo(
    () => generateMenuItems(setActiveKey, selectedWidgetId),
    [selectedWidgetId]
  );

  if (!isLoggedIn) {
    return (
      <div className='login-page'>
        <div className='Button-container'>
          <Button onClick={() => (window.location.href = loginUrl)}>Ingresar</Button>
        </div>
      </div>
    );
  }

  return (
    <Layout style={{ display: 'flex', height: '100vh' }}>
      <AppSider
        activeKey={activeKey}
        items={items}
        userInfo={userInfo}
        onKeySelect={setActiveKey}
        onSignOut={signOut}
      />
      <Layout>
        <Content
          style={{
            paddingLeft: '24px',
            paddingRight: '24px',
            minHeight: 280,
            overflow: 'auto'
          }}
        >
          {components[activeKey]}
        </Content>
      </Layout>
    </Layout>
  );
};
